import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaAward,
  FaCheckCircle,
  FaCog,
  FaDesktop,
  FaDiscord,
  FaEnvelopeOpenText,
  FaExclamationCircle,
  FaFlag,
  FaKey,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaStar,
  FaSpinner,
  FaTimes,
  FaTrashAlt,
  FaUserCheck,
  FaUserCog,
  FaUserEdit,
  FaUserShield,
  FaUserSlash,
  FaUsers,
  FaClipboardList,
  FaWallet,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { contactService, settingsService, socialService, studioService, userService } from '../../services/apiService';
import VihsMark from '../../components/VihsMark';
import './Admin.css';

const PROJECT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_review', label: 'Em análise' },
  { value: 'awaiting_client', label: 'Aguardando cliente' },
  { value: 'quoted', label: 'Proposta enviada' },
  { value: 'accepted', label: 'Aceito' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'completed', label: 'Concluído' },
  { value: 'declined', label: 'Recusado' },
  { value: 'cancelled', label: 'Cancelado' },
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return '—';
  }
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

function maskBrDate(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
  return parts.filter(Boolean).join('/');
}

function brDateToIso(value) {
  const [day, month, year] = String(value || '').split('/');
  if (!day || !month || !year || year.length < 4) return '';
  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  return Number.isNaN(new Date(iso).getTime()) ? '' : iso;
}

function isoToBrDate(value) {
  const raw = String(value || '');
  if (!raw) return '';
  if (raw.includes('/')) return raw;
  const [year, month, day] = raw.slice(0, 10).split('-');
  if (!year || !month || !day) return '';
  return `${day}/${month}/${year}`;
}

function normalizeDiscordAvatarUrl(url) {
  const value = String(url || '').trim();
  if (!value) return '';
  if (!/cdn\.discordapp\.com\/avatars\//i.test(value)) return value;
  try {
    const parsed = new URL(value);
    parsed.searchParams.set('size', '256');
    return parsed.toString();
  } catch {
    return value;
  }
}

const FLAG_OPTIONS = [
  { value: '', label: 'Todos os filtros' },
  { value: 'mustChangePassword', label: 'Reset pendente' },
  { value: 'unverified', label: 'E-mail pendente' },
  { value: 'discord', label: 'Com Discord' },
];

const EMPTY_MEMBER_FORM = {
  name: '',
  role: '',
  focus: '',
  bio: '',
  avatarUrl: '',
  linkedUserId: '',
  useDiscordAvatar: false,
  discordUrl: '',
  instagramUrl: '',
  order: 0,
  active: true,
};

const EMPTY_BADGE_FORM = {
  name: '',
  icon: 'shield',
  color: '#80c2e8',
  backgroundColor: '#12334a',
  description: '',
  order: 0,
  active: true,
};

const EMPTY_SYSTEM_FORM = {
  enabled: false,
  autoFollow: true,
  featured: true,
  displayName: '',
  bio: '',
  avatarUrl: '',
  coverUrl: '',
  operatingSince: '',
};

const BADGE_ICONS = {
  shield: FaShieldAlt,
  award: FaAward,
  star: FaStar,
  crown: FaAward,
  gavel: FaShieldAlt,
  sparkles: FaStar,
  heart: FaStar,
};

function memberToForm(member) {
  return {
    name: member?.name || '',
    role: member?.role || '',
    focus: member?.focus || '',
    bio: member?.bio || '',
    avatarUrl: member?.avatarUrl || '',
    linkedUserId: member?.linkedUserId || '',
    useDiscordAvatar: Boolean(member?.useDiscordAvatar),
    discordUrl: member?.discordUrl || '',
    instagramUrl: member?.instagramUrl || '',
    order: member?.order ?? 0,
    active: member?.active !== false,
  };
}

export default function Admin() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState('');
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    active: 0,
    disabled: 0,
    mustChangePassword: 0,
    unverified: 0,
    discordLinked: 0,
  });
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 40, total: 0, pages: 1 });
  const [selectedId, setSelectedId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [filters, setFilters] = useState({
    q: '',
    role: '',
    status: '',
    flag: '',
    page: 1,
  });
  const [searchInput, setSearchInput] = useState('');

  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamEditor, setTeamEditor] = useState(null);
  const [teamForm, setTeamForm] = useState(EMPTY_MEMBER_FORM);
  const [discordUsers, setDiscordUsers] = useState([]);
  const [discordUsersLoading, setDiscordUsersLoading] = useState(false);
  const [badges, setBadges] = useState([]);
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeEditor, setBadgeEditor] = useState(null);
  const [badgeForm, setBadgeForm] = useState(EMPTY_BADGE_FORM);
  const [systemForm, setSystemForm] = useState(EMPTY_SYSTEM_FORM);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [projectRequests, setProjectRequests] = useState([]);
  const [projectCounts, setProjectCounts] = useState({});
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectFilters, setProjectFilters] = useState({ status: '', q: '', page: 1 });
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectForm, setProjectForm] = useState({
    status: 'pending',
    clientMessage: '',
    adminNotes: '',
    historyNote: '',
  });
  const [siteSettings, setSiteSettings] = useState({
    maintenanceMode: false,
    devHubPublicAccess: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const linkedDiscordUser = useMemo(
    () => discordUsers.find((item) => item.id === teamForm.linkedUserId) || null,
    [discordUsers, teamForm.linkedUserId]
  );

  const showFlash = useCallback((type, message) => {
    setFlash({ type, message });
    window.setTimeout(() => setFlash(null), 3200);
  }, []);

  const load = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const response = await userService.getAdminOverview({
        q: nextFilters.q || undefined,
        role: nextFilters.role || undefined,
        status: nextFilters.status || undefined,
        flag: nextFilters.flag || undefined,
        page: nextFilters.page || 1,
        limit: 40,
      });
      setStats(response?.data?.stats || {});
      setUsers(response?.data?.users || []);
      setPagination(response?.data?.pagination || { page: 1, limit: 40, total: 0, pages: 1 });
    } catch (err) {
      setError(err.message || 'Não foi possível carregar o painel.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadTeam = useCallback(async () => {
    setTeamLoading(true);
    setError('');
    try {
      const response = await studioService.listTeamAdmin();
      setTeam(response?.data?.team || []);
    } catch (err) {
      setError(err.message || 'Não foi possível carregar a equipe.');
    } finally {
      setTeamLoading(false);
    }
  }, []);

  const loadBadges = useCallback(async () => {
    setBadgeLoading(true);
    try {
      const response = await userService.listBadges();
      setBadges(response?.data?.badges || []);
    } catch (err) {
      setError(err.message || 'Não foi possível carregar os cargos.');
    } finally {
      setBadgeLoading(false);
    }
  }, []);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    setError('');
    try {
      const response = await socialService.getAdminReports({ status: 'pending', limit: 50 });
      setReports(response?.data?.reports || []);
    } catch (err) {
      setError(err.message || 'Não foi possível carregar as denúncias.');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const loadProjectRequests = useCallback(async () => {
    setProjectsLoading(true);
    setError('');
    try {
      const response = await contactService.listAdminRequests({
        status: projectFilters.status || undefined,
        q: projectFilters.q || undefined,
        page: projectFilters.page,
        limit: 30,
      });
      const list = response?.data?.requests || [];
      setProjectRequests(list);
      setProjectCounts(response?.data?.counts || {});
      setSelectedProjectId((current) => {
        if (current && list.some((item) => item.id === current)) return current;
        return list[0]?.id || null;
      });
    } catch (err) {
      setError(err.message || 'Não foi possível carregar os pedidos.');
    } finally {
      setProjectsLoading(false);
    }
  }, [projectFilters]);

  const loadSiteSettings = useCallback(async () => {
    setSettingsLoading(true);
    setError('');
    try {
      const response = await settingsService.getPublic();
      setSiteSettings(response?.data?.settings || {
        maintenanceMode: false,
        devHubPublicAccess: false,
      });
    } catch (err) {
      setError(err.message || 'Não foi possível carregar as configurações.');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'users') load(filters);
  }, [tab, filters, load]);

  useEffect(() => {
    if (tab === 'team') loadTeam();
  }, [tab, loadTeam]);

  useEffect(() => {
    if (tab === 'badges' || tab === 'users') loadBadges();
  }, [tab, loadBadges]);

  useEffect(() => {
    if (tab === 'reports') loadReports();
  }, [tab, loadReports]);

  useEffect(() => {
    if (tab === 'projects') loadProjectRequests();
  }, [tab, loadProjectRequests]);

  useEffect(() => {
    if (tab === 'settings') loadSiteSettings();
  }, [tab, loadSiteSettings]);

  const selectedProject = useMemo(
    () => projectRequests.find((item) => item.id === selectedProjectId) || null,
    [projectRequests, selectedProjectId]
  );

  useEffect(() => {
    if (!selectedProject) {
      setProjectForm({
        status: 'pending',
        clientMessage: '',
        adminNotes: '',
        historyNote: '',
      });
      return;
    }
    setProjectForm({
      status: selectedProject.status || 'pending',
      clientMessage: selectedProject.clientMessage || '',
      adminNotes: selectedProject.adminNotes || '',
      historyNote: '',
    });
  }, [selectedProject]);

  useEffect(() => {
    if (!teamEditor) {
      setDiscordUsers([]);
      return undefined;
    }

    let cancelled = false;

    async function loadDiscordUsers() {
      setDiscordUsersLoading(true);
      try {
        const response = await userService.getAdminOverview({
          flag: 'discord',
          status: 'active',
          limit: 60,
        });
        if (cancelled) return;
        const list = (response?.data?.users || []).filter((item) => item.discordId);
        setDiscordUsers(list);
      } catch {
        if (!cancelled) setDiscordUsers([]);
      } finally {
        if (!cancelled) setDiscordUsersLoading(false);
      }
    }

    loadDiscordUsers();
    return () => {
      cancelled = true;
    };
  }, [teamEditor]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        if (current.q === searchInput) return current;
        return { ...current, q: searchInput, page: 1 };
      });
    }, 320);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const selected = useMemo(
    () => users.find((item) => item.id === selectedId) || null,
    [users, selectedId]
  );

  useEffect(() => {
    if (!selected) {
      setSystemForm(EMPTY_SYSTEM_FORM);
      return;
    }
    const system = selected.systemAccount || {};
    setSystemForm({
      enabled: Boolean(system.enabled),
      autoFollow: system.autoFollow !== false,
      featured: system.featured !== false,
      displayName: selected.profile?.displayName || '',
      bio: selected.profile?.bio || '',
      avatarUrl: selected.avatarUrl || '',
      coverUrl: system.coverUrl || '',
      operatingSince: isoToBrDate(system.operatingSince),
    });
  }, [selectedId, selected]);

  const cards = [
    { key: 'total', label: 'Contas', value: stats.total, icon: FaUsers, filter: {} },
    { key: 'active', label: 'Ativas', value: stats.active, icon: FaUserCheck, filter: { status: 'active', flag: '' } },
    { key: 'admins', label: 'Admins', value: stats.admins, icon: FaShieldAlt, filter: { role: 'admin', flag: '' } },
    { key: 'mustChangePassword', label: 'Reset pendente', value: stats.mustChangePassword, icon: FaKey, filter: { flag: 'mustChangePassword', role: '', status: '' } },
    { key: 'unverified', label: 'E-mail pendente', value: stats.unverified, icon: FaEnvelopeOpenText, filter: { flag: 'unverified', role: '', status: '' } },
    { key: 'disabled', label: 'Desativadas', value: stats.disabled, icon: FaUserSlash, filter: { status: 'disabled', flag: '' } },
  ];

  const mergeUser = (nextUser) => {
    setUsers((current) => current.map((item) => (
      item.id === nextUser.id
        ? { ...item, ...nextUser, activeSessions: nextUser.activeSessions ?? item.activeSessions }
        : item
    )));
  };

  const runAction = async (key, action, successMessage) => {
    setActing(key);
    setError('');
    try {
      const response = await action();
      if (response?.data?.user) mergeUser(response.data.user);
      showFlash('success', response?.message || successMessage);
      setConfirm(null);
      await load(filters);
    } catch (err) {
      setError(err.message || 'Falha ao executar a ação.');
      showFlash('error', err.message || 'Falha ao executar a ação.');
    } finally {
      setActing('');
    }
  };

  const openConfirm = (action) => setConfirm(action);

  const openCreateMember = () => {
    setSelectedId(null);
    setTeamEditor({ mode: 'create', id: null });
    setTeamForm({
      ...EMPTY_MEMBER_FORM,
      order: team.length,
    });
  };

  const openEditMember = (member) => {
    setSelectedId(null);
    setTeamEditor({ mode: 'edit', id: member.id });
    setTeamForm(memberToForm(member));
  };

  const closeTeamEditor = () => {
    setTeamEditor(null);
    setTeamForm(EMPTY_MEMBER_FORM);
  };

  const updateTeamField = (field, value) => {
    setTeamForm((current) => ({ ...current, [field]: value }));
  };

  const applyLinkedDiscordProfile = (user, { fillName = false } = {}) => {
    if (!user) return;
    const avatarUrl = normalizeDiscordAvatarUrl(user.avatarUrl);
    setTeamForm((current) => ({
      ...current,
      linkedUserId: user.id,
      useDiscordAvatar: true,
      avatarUrl: avatarUrl || current.avatarUrl,
      name: fillName && !current.name.trim()
        ? (user.profile?.displayName || user.username || current.name)
        : current.name,
    }));
  };

  const handleLinkedUserChange = (userId) => {
    if (!userId) {
      setTeamForm((current) => ({
        ...current,
        linkedUserId: '',
        useDiscordAvatar: false,
      }));
      return;
    }

    const user = discordUsers.find((item) => item.id === userId);
    if (!user) {
      updateTeamField('linkedUserId', userId);
      return;
    }

    if (teamForm.useDiscordAvatar) {
      applyLinkedDiscordProfile(user);
    } else {
      updateTeamField('linkedUserId', userId);
    }
  };

  const handleDiscordAvatarToggle = (enabled) => {
    if (!enabled) {
      updateTeamField('useDiscordAvatar', false);
      return;
    }

    if (!teamForm.linkedUserId) {
      showFlash('error', 'Selecione primeiro a conta com Discord vinculada.');
      return;
    }

    const user = discordUsers.find((item) => item.id === teamForm.linkedUserId);
    if (!user) {
      showFlash('error', 'Conta vinculada não encontrada.');
      return;
    }
    if (!user.avatarUrl) {
      showFlash('error', 'Essa conta não possui foto de perfil do Discord.');
      return;
    }

    applyLinkedDiscordProfile(user, { fillName: true });
    showFlash('success', 'Foto do Discord aplicada automaticamente.');
  };

  const saveTeamMember = async (event) => {
    event.preventDefault();
    if (!teamEditor) return;

    const key = teamEditor.mode === 'create' ? 'team-create' : `team-save-${teamEditor.id}`;
    setActing(key);
    setError('');
    try {
      const payload = {
        name: teamForm.name.trim(),
        role: teamForm.role.trim(),
        focus: teamForm.focus.trim(),
        bio: teamForm.bio.trim(),
        avatarUrl: teamForm.useDiscordAvatar
          ? null
          : (normalizeDiscordAvatarUrl(teamForm.avatarUrl) || null),
        linkedUserId: teamForm.linkedUserId || null,
        useDiscordAvatar: Boolean(teamForm.useDiscordAvatar),
        discordUrl: teamForm.discordUrl.trim() || null,
        instagramUrl: teamForm.instagramUrl.trim() || null,
        order: Number(teamForm.order) || 0,
        active: Boolean(teamForm.active),
      };

      const response = teamEditor.mode === 'create'
        ? await studioService.createMember(payload)
        : await studioService.updateMember(teamEditor.id, payload);

      showFlash('success', response?.message || 'Equipe atualizada.');
      closeTeamEditor();
      await loadTeam();
    } catch (err) {
      setError(err.message || 'Falha ao salvar membro.');
      showFlash('error', err.message || 'Falha ao salvar membro.');
    } finally {
      setActing('');
    }
  };

  const toggleMemberActive = async (member) => {
    const key = `team-active-${member.id}`;
    setActing(key);
    setError('');
    try {
      const response = await studioService.updateMember(member.id, { active: !member.active });
      showFlash('success', response?.message || (member.active ? 'Membro ocultado.' : 'Membro ativado.'));
      await loadTeam();
    } catch (err) {
      setError(err.message || 'Falha ao atualizar status.');
      showFlash('error', err.message || 'Falha ao atualizar status.');
    } finally {
      setActing('');
    }
  };

  const deleteTeamMember = async (member) => {
    const key = `team-delete-${member.id}`;
    setActing(key);
    setError('');
    try {
      const response = await studioService.deleteMember(member.id);
      showFlash('success', response?.message || 'Membro removido.');
      setConfirm(null);
      if (teamEditor?.id === member.id) closeTeamEditor();
      await loadTeam();
    } catch (err) {
      setError(err.message || 'Falha ao remover membro.');
      showFlash('error', err.message || 'Falha ao remover membro.');
    } finally {
      setActing('');
    }
  };

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setError('');
    setSelectedId(null);
    setSelectedProjectId(null);
    closeTeamEditor();
    setBadgeEditor(null);
    setConfirm(null);
  };

  const saveProjectRequest = async (event) => {
    event.preventDefault();
    if (!selectedProject) return;
    const key = `project-${selectedProject.id}`;
    setActing(key);
    setError('');
    try {
      const response = await contactService.updateAdminRequest(selectedProject.id, projectForm);
      showFlash('success', response?.message || 'Pedido atualizado.');
      await loadProjectRequests();
    } catch (err) {
      setError(err.message || 'Falha ao atualizar pedido.');
      showFlash('error', err.message || 'Falha ao atualizar pedido.');
    } finally {
      setActing('');
    }
  };

  const handleReviewReport = async (reportId, status) => {
    setActing(`report-${reportId}`);
    try {
      await socialService.reviewReport(reportId, status);
      setReports((current) => current.filter((report) => report.id !== reportId));
      showFlash(
        'success',
        status === 'reviewed'
          ? 'Denúncia confirmada. O denunciante foi notificado.'
          : 'Denúncia dispensada. O denunciante foi notificado.'
      );
    } catch (err) {
      setError(err.message || 'Não foi possível revisar a denúncia.');
    } finally {
      setActing('');
    }
  };

  const toggleMaintenanceMode = async () => {
    const nextValue = !siteSettings.maintenanceMode;
    setSettingsLoading(true);
    try {
      const response = await settingsService.update({ maintenanceMode: nextValue });
      setSiteSettings(response?.data?.settings || {
        ...siteSettings,
        maintenanceMode: nextValue,
      });
      showFlash(
        'success',
        nextValue
          ? (siteSettings.devHubPublicAccess
            ? 'Modo em breve ativado. O Dev Hub permanece público; o restante do site fica restrito.'
            : 'Modo em breve ativado. Somente administradores e a página de login continuam acessíveis.')
          : 'Modo em breve desativado.'
      );
    } catch (err) {
      setError(err.message || 'Não foi possível salvar a configuração.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleDevHubPublicAccess = async () => {
    const nextValue = !siteSettings.devHubPublicAccess;
    setSettingsLoading(true);
    try {
      const response = await settingsService.update({ devHubPublicAccess: nextValue });
      setSiteSettings(response?.data?.settings || {
        ...siteSettings,
        devHubPublicAccess: nextValue,
      });
      showFlash(
        'success',
        nextValue
          ? 'Prévia do Dev Hub ativada. Todos podem acessar /dev-hub e perfis públicos; o restante fica restrito.'
          : 'Prévia do Dev Hub desativada.'
      );
    } catch (err) {
      setError(err.message || 'Não foi possível salvar a configuração.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const teamDrawerOpen = Boolean(teamEditor);
  const userDrawerOpen = tab === 'users' && Boolean(selected);
  const isSavingTeam = acting === 'team-create' || (teamEditor?.id && acting === `team-save-${teamEditor.id}`);

  const saveBadge = async (event) => {
    event.preventDefault();
    const key = badgeEditor?.id ? `badge-${badgeEditor.id}` : 'badge-create';
    setActing(key);
    try {
      const payload = { ...badgeForm, name: badgeForm.name.trim(), description: badgeForm.description.trim(), order: Number(badgeForm.order) || 0 };
      const response = badgeEditor?.id
        ? await userService.updateBadge(badgeEditor.id, payload)
        : await userService.createBadge(payload);
      showFlash('success', response?.message || 'Cargo salvo.');
      setBadgeEditor(null);
      setBadgeForm(EMPTY_BADGE_FORM);
      await loadBadges();
    } catch (err) {
      showFlash('error', err.message || 'Não foi possível salvar o cargo.');
    } finally {
      setActing('');
    }
  };

  const toggleUserBadge = async (badgeId) => {
    if (!selected) return;
    const ids = new Set((selected.badges || []).map((badge) => badge.id));
    if (ids.has(badgeId)) ids.delete(badgeId); else ids.add(badgeId);
    await runAction(`badges-${selected.id}`, () => userService.updateUserBadges(selected.id, [...ids]), 'Cargos atualizados.');
  };

  return (
    <main className="admin-page">
      <div className="admin-atmosphere" aria-hidden="true">
        <div className="admin-orb admin-orb-one" />
        <div className="admin-orb admin-orb-two" />
        <div className="admin-grid-bg" />
      </div>

      <header className="admin-topbar">
        <Link to="/" className="admin-brand" aria-label="Vih's — página inicial">
          <VihsMark />
          Vih&apos;s
        </Link>
        <div className="admin-topbar-actions">
          {me?.canAccessFinance && (
            <Link to="/financas" className="admin-back">
              <FaWallet aria-hidden="true" />
              Finanças
            </Link>
          )}
          <Link to="/conta" className="admin-back">
            <FaArrowLeft aria-hidden="true" />
            Voltar à conta
          </Link>
        </div>
      </header>

      <div className="admin-shell">
        <motion.section
          className="admin-hero"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div>
            <p className="admin-eyebrow">Moderação</p>
            <h1>Painel admin</h1>
            <p className="admin-lead">
              {tab === 'users'
                ? 'Gerencie cargos, sessões, status de contas e resets forçados de senha com controle total.'
                : 'Cadastre e organize os membros exibidos na página pública do Studio.'}
            </p>
          </div>
          <div className="admin-hero-meta">
            <span className="admin-badge">Administrador</span>
            <p>@{me?.username}</p>
          </div>
        </motion.section>

        <nav className="admin-tabs" aria-label="Seções do painel">
          <button
            type="button"
            className={tab === 'users' ? 'is-active' : ''}
            onClick={() => switchTab('users')}
          >
            <FaUsers aria-hidden="true" />
            Usuários
          </button>
          <button
            type="button"
            className={tab === 'team' ? 'is-active' : ''}
            onClick={() => switchTab('team')}
          >
            <FaUserEdit aria-hidden="true" />
            Equipe do Studio
          </button>
          <button
            type="button"
            className={tab === 'badges' ? 'is-active' : ''}
            onClick={() => switchTab('badges')}
          >
            <FaAward aria-hidden="true" />
            Cargos e badges
          </button>
          <button
            type="button"
            className={tab === 'reports' ? 'is-active' : ''}
            onClick={() => switchTab('reports')}
          >
            <FaFlag aria-hidden="true" />
            Denúncias
          </button>
          <button
            type="button"
            className={tab === 'projects' ? 'is-active' : ''}
            onClick={() => switchTab('projects')}
          >
            <FaClipboardList aria-hidden="true" />
            Pedidos
            {projectCounts.pending ? ` (${projectCounts.pending})` : ''}
          </button>
          <button
            type="button"
            className={tab === 'settings' ? 'is-active' : ''}
            onClick={() => switchTab('settings')}
          >
            <FaCog aria-hidden="true" />
            Configurações
          </button>
        </nav>

        <AnimatePresence>
          {flash && (
            <motion.div
              className={`admin-toast ${flash.type}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              role="status"
            >
              {flash.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
              <span>{flash.message}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {error && !flash && (
          <div className="admin-alert" role="alert">
            <FaExclamationCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {tab === 'users' && (
          <>
            <section className="admin-stats" aria-label="Resumo">
              {cards.map((card) => {
                const Icon = card.icon;
                const isActive = Object.entries(card.filter).every(([key, value]) => (
                  !value || filters[key] === value
                )) && (card.key === 'total'
                  ? !filters.role && !filters.status && !filters.flag
                  : true);
                return (
                  <button
                    key={card.key}
                    type="button"
                    className={`admin-stat ${isActive && card.key !== 'total' ? 'is-active' : ''}`}
                    onClick={() => setFilters((current) => ({
                      ...current,
                      ...card.filter,
                      page: 1,
                      ...(card.key === 'total' ? { role: '', status: '', flag: '' } : {}),
                    }))}
                  >
                    <div className="admin-stat-icon" aria-hidden="true">
                      <Icon />
                    </div>
                    <div>
                      <p>{card.label}</p>
                      <strong>{loading ? '—' : card.value ?? 0}</strong>
                    </div>
                  </button>
                );
              })}
            </section>

            <section className="admin-card">
              <div className="admin-card-head">
                <div>
                  <span className="admin-card-eyebrow">Contas</span>
                  <h2>Moderação de usuários</h2>
                  <p>Busque, filtre e execute ações de segurança em tempo real.</p>
                </div>
                <span className="admin-count">{loading ? '…' : pagination.total}</span>
              </div>

              <div className="admin-toolbar">
                <label className="admin-search">
                  <FaSearch aria-hidden="true" />
                  <input
                    type="search"
                    placeholder="Buscar por usuário, e-mail ou nome..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </label>

                <select
                  value={filters.role}
                  onChange={(e) => setFilters((current) => ({ ...current, role: e.target.value, page: 1 }))}
                  aria-label="Filtrar por cargo"
                >
                  <option value="">Todos os cargos</option>
                  <option value="admin">Administradores</option>
                  <option value="user">Membros</option>
                </select>

                <select
                  value={filters.status}
                  onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value, page: 1 }))}
                  aria-label="Filtrar por status"
                >
                  <option value="">Todos os status</option>
                  <option value="active">Ativas</option>
                  <option value="disabled">Desativadas</option>
                </select>

                <select
                  value={filters.flag}
                  onChange={(e) => setFilters((current) => ({ ...current, flag: e.target.value, page: 1 }))}
                  aria-label="Filtrar por sinalização"
                >
                  {FLAG_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {loading ? (
                <p className="admin-muted admin-loading-line">
                  <FaSpinner className="admin-spin" /> Carregando usuários...
                </p>
              ) : users.length === 0 ? (
                <p className="admin-muted">Nenhuma conta encontrada com esses filtros.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Usuário</th>
                        <th>Cargo</th>
                        <th>Status</th>
                        <th>Sessões</th>
                        <th>Último acesso</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((item) => {
                        const isMe = item.id === me?.id;
                        return (
                          <tr
                            key={item.id}
                            className={selectedId === item.id ? 'is-selected' : ''}
                            onClick={() => setSelectedId(item.id)}
                          >
                            <td>
                              <div className="admin-user-cell">
                                <div className="admin-avatar" aria-hidden="true">
                                  {item.avatarUrl ? <img src={item.avatarUrl} alt="" /> : <span>{getInitials(item.profile?.displayName || item.username)}</span>}
                                </div>
                                <div>
                                  <strong>
                                    {item.profile?.displayName || item.username}
                                    {isMe ? ' (você)' : ''}
                                  </strong>
                                  <span>@{item.username} · {item.email}</span>
                                  <div className="admin-inline-flags">
                                    {item.mustChangePassword && <em>Reset pendente</em>}
                                    {!item.emailVerified && <em className="is-warn">E-mail pendente</em>}
                                    {item.discordId && <em className="is-discord"><FaDiscord /> Discord</em>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`admin-pill ${item.role === 'admin' ? 'is-admin' : ''}`}>
                                {item.role === 'admin' ? 'Admin' : 'Membro'}
                              </span>
                            </td>
                            <td>
                              <span className={`admin-pill ${item.status === 'active' ? 'is-ok' : 'is-warn'}`}>
                                {item.status === 'active' ? 'Ativa' : 'Desativada'}
                              </span>
                            </td>
                            <td>
                              <span className="admin-sessions">
                                <FaDesktop aria-hidden="true" />
                                {item.activeSessions ?? 0}
                              </span>
                            </td>
                            <td>{formatDate(item.lastLoginAt)}</td>
                            <td>
                              <button
                                type="button"
                                className="admin-row-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedId(item.id);
                                }}
                              >
                                Gerenciar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination.pages > 1 && (
                <div className="admin-pagination">
                  <button
                    type="button"
                    disabled={pagination.page <= 1 || loading}
                    onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
                  >
                    Anterior
                  </button>
                  <span>Página {pagination.page} de {pagination.pages}</span>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.pages || loading}
                    onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {tab === 'team' && (
          <section className="admin-card">
            <div className="admin-card-head">
              <div>
                <span className="admin-card-eyebrow">Studio</span>
                <h2>Equipe pública</h2>
                <p>Membros exibidos em /studio — adicione, edite, oculte ou remova.</p>
              </div>
              <div className="admin-card-actions">
                <span className="admin-count">{teamLoading ? '…' : team.length}</span>
                <button type="button" className="admin-btn primary" onClick={openCreateMember}>
                  <FaPlus aria-hidden="true" />
                  Adicionar membro
                </button>
              </div>
            </div>

            {teamLoading ? (
              <p className="admin-muted admin-loading-line">
                <FaSpinner className="admin-spin" /> Carregando equipe...
              </p>
            ) : team.length === 0 ? (
              <p className="admin-muted">Nenhum membro cadastrado. Adicione o primeiro da equipe.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Membro</th>
                      <th>Cargo</th>
                      <th>Ordem</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.map((member) => (
                      <tr
                        key={member.id}
                        className={teamEditor?.id === member.id ? 'is-selected' : ''}
                        onClick={() => openEditMember(member)}
                      >
                        <td>
                          <div className="admin-user-cell">
                            <div className="admin-avatar" aria-hidden="true">
                              {member.avatarUrl
                                ? <img src={member.avatarUrl} alt="" />
                                : <span>{getInitials(member.name)}</span>}
                            </div>
                            <div>
                              <strong>{member.name}</strong>
                              <span>{member.focus || 'Sem foco definido'}</span>
                            </div>
                          </div>
                        </td>
                        <td>{member.role}</td>
                        <td>{member.order ?? 0}</td>
                        <td>
                          <span className={`admin-pill ${member.active ? 'is-ok' : 'is-warn'}`}>
                            {member.active ? 'Visível' : 'Oculto'}
                          </span>
                        </td>
                        <td>
                          <div className="admin-row-actions">
                            <button
                              type="button"
                              className="admin-row-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditMember(member);
                              }}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="admin-row-btn"
                              disabled={acting !== ''}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMemberActive(member);
                              }}
                            >
                              {member.active ? 'Ocultar' : 'Exibir'}
                            </button>
                            <button
                              type="button"
                              className="admin-row-btn is-danger"
                              disabled={acting !== ''}
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfirm({
                                  title: 'Remover membro',
                                  description: `Remover ${member.name} da equipe pública do Studio?`,
                                  tone: 'danger',
                                  key: `team-delete-${member.id}`,
                                  run: () => deleteTeamMember(member),
                                });
                              }}
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === 'reports' && (
          <section className="admin-card">
            <div className="admin-card-head">
              <div>
                <span className="admin-card-eyebrow">Moderação</span>
                <h2>Fila de denúncias</h2>
                <p>Analise denúncias de usuários e publicações enviadas pela comunidade.</p>
              </div>
              <button type="button" className="admin-btn" onClick={loadReports} disabled={reportsLoading}>
                {reportsLoading ? <FaSpinner className="admin-spin" /> : 'Atualizar'}
              </button>
            </div>
            {reportsLoading ? (
              <div className="admin-empty">Carregando denúncias…</div>
            ) : reports.length === 0 ? (
              <div className="admin-empty">Não há denúncias pendentes.</div>
            ) : (
              <div className="admin-reports">
                {reports.map((report) => {
                  const target = report.targetType === 'user' ? report.targetUser : report.post;
                  return (
                    <article className="admin-report" key={report.id}>
                      <div>
                        <span className="admin-report-type">{report.targetType === 'post' ? 'Publicação' : 'Usuário'} · {report.reason}</span>
                        <strong>Denunciado por @{report.reporter?.username || 'conta removida'}</strong>
                        <p>
                          {report.targetType === 'user'
                            ? `Conta: @${target?.username || 'removida'}`
                            : `Post de @${report.post?.author?.username || 'removido'}: “${target?.content || 'publicação removida'}”`}
                        </p>
                        {report.details && <small>{report.details}</small>}
                      </div>
                      <div className="admin-report-actions">
                        <button
                          type="button"
                          className="admin-row-btn"
                          disabled={acting === `report-${report.id}`}
                          onClick={() => handleReviewReport(report.id, 'dismissed')}
                        >
                          Dispensar
                        </button>
                        <button
                          type="button"
                          className="admin-btn primary"
                          disabled={acting === `report-${report.id}`}
                          onClick={() => handleReviewReport(report.id, 'reviewed')}
                        >
                          {acting === `report-${report.id}` ? <FaSpinner className="admin-spin" /> : 'Confirmar'}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {tab === 'projects' && (
          <section className="admin-card admin-projects-panel">
            <div className="admin-card-head">
              <div>
                <span className="admin-card-eyebrow">Studio</span>
                <h2>Pedidos de projeto</h2>
                <p>Gerencie status, mensagens ao cliente e histórico de cada solicitação.</p>
              </div>
              <div className="admin-card-actions">
                <span className="admin-count">{projectCounts.pending || 0}</span>
                <button type="button" className="admin-btn" onClick={loadProjectRequests} disabled={projectsLoading}>
                  {projectsLoading ? <FaSpinner className="admin-spin" /> : 'Atualizar'}
                </button>
              </div>
            </div>

            <div className="admin-projects-toolbar">
              <label className="admin-search">
                <FaSearch aria-hidden="true" />
                <input
                  value={projectFilters.q}
                  onChange={(e) => setProjectFilters((current) => ({
                    ...current,
                    q: e.target.value,
                    page: 1,
                  }))}
                  placeholder="Buscar por código, nome ou e-mail"
                  aria-label="Buscar pedidos"
                />
              </label>
              <select
                value={projectFilters.status}
                onChange={(e) => setProjectFilters((current) => ({
                  ...current,
                  status: e.target.value,
                  page: 1,
                }))}
                aria-label="Filtrar por status"
              >
                <option value="">Todos os status</option>
                {PROJECT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                    {projectCounts[option.value] ? ` (${projectCounts[option.value]})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {projectsLoading ? (
              <div className="admin-empty">Carregando pedidos…</div>
            ) : projectRequests.length === 0 ? (
              <div className="admin-empty">Nenhum pedido encontrado.</div>
            ) : (
              <div className="admin-projects-workspace">
                <aside className="admin-projects-rail" aria-label="Lista de pedidos">
                  {projectRequests.map((request) => {
                    const tone = ['completed', 'accepted', 'in_progress'].includes(request.status)
                      ? 'ok'
                      : ['declined', 'cancelled'].includes(request.status)
                        ? 'danger'
                        : 'warn';
                    return (
                      <button
                        key={request.id}
                        type="button"
                        className={`admin-projects-rail-item${selectedProjectId === request.id ? ' is-active' : ''}`}
                        onClick={() => setSelectedProjectId(request.id)}
                      >
                        <div className="admin-projects-rail-top">
                          <strong>{request.code}</strong>
                          <span className={`admin-projects-chip is-${tone}`}>{request.statusLabel}</span>
                        </div>
                        <p>{request.company || request.name}</p>
                        <small>{request.email}</small>
                      </button>
                    );
                  })}
                </aside>

                {selectedProject && (
                  <form className="admin-projects-editor" onSubmit={saveProjectRequest}>
                    <header className="admin-projects-editor-head">
                      <div>
                        <span className="admin-projects-code">{selectedProject.code}</span>
                        <h3>{selectedProject.company || selectedProject.name}</h3>
                        <p>
                          {selectedProject.email}
                          {selectedProject.user
                            ? ` · @${selectedProject.user.username}`
                            : ' · sem conta vinculada'}
                        </p>
                        <p className="admin-projects-opened">
                          Aberto em {formatDate(selectedProject.createdAt)}
                          {selectedProject.respondedAt
                            ? ` · Respondido em ${formatDate(selectedProject.respondedAt)}`
                            : ''}
                        </p>
                      </div>
                      <span className={`admin-projects-chip is-${
                        ['completed', 'accepted', 'in_progress'].includes(selectedProject.status)
                          ? 'ok'
                          : ['declined', 'cancelled'].includes(selectedProject.status)
                            ? 'danger'
                            : 'warn'
                      }`}
                      >
                        {selectedProject.statusLabel}
                      </span>
                    </header>

                    <div className="admin-projects-brief">
                      <span>Briefing</span>
                      <p>{selectedProject.description}</p>
                      {selectedProject.services?.length ? (
                        <ul className="admin-projects-tags">
                          {selectedProject.services.map((service) => (
                            <li key={service}>{service}</li>
                          ))}
                        </ul>
                      ) : null}
                      <dl className="admin-projects-facts">
                        <div>
                          <dt>Orçamento</dt>
                          <dd>{selectedProject.budget || '—'}</dd>
                        </div>
                        <div>
                          <dt>Prazo</dt>
                          <dd>{selectedProject.timeline || '—'}</dd>
                        </div>
                        <div>
                          <dt>Discord</dt>
                          <dd>{selectedProject.discord || '—'}</dd>
                        </div>
                        <div>
                          <dt>Telefone</dt>
                          <dd>{selectedProject.phone || '—'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="admin-projects-fields">
                      <label className="admin-field">
                        <span>Status</span>
                        <select
                          value={projectForm.status}
                          onChange={(e) => setProjectForm((current) => ({
                            ...current,
                            status: e.target.value,
                          }))}
                        >
                          {PROJECT_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>

                      <label className="admin-field">
                        <span>Mensagem para o cliente</span>
                        <textarea
                          rows={3}
                          value={projectForm.clientMessage}
                          onChange={(e) => setProjectForm((current) => ({
                            ...current,
                            clientMessage: e.target.value,
                          }))}
                          placeholder="Aparece na conta do cliente"
                        />
                      </label>

                      <label className="admin-field">
                        <span>Nota do histórico</span>
                        <textarea
                          rows={2}
                          value={projectForm.historyNote}
                          onChange={(e) => setProjectForm((current) => ({
                            ...current,
                            historyNote: e.target.value,
                          }))}
                          placeholder="Ex.: Enviamos a proposta por e-mail."
                        />
                      </label>

                      <label className="admin-field">
                        <span>Notas internas</span>
                        <textarea
                          rows={3}
                          value={projectForm.adminNotes}
                          onChange={(e) => setProjectForm((current) => ({
                            ...current,
                            adminNotes: e.target.value,
                          }))}
                          placeholder="Observações só da equipe"
                        />
                      </label>
                    </div>

                    <div className="admin-form-actions">
                      <button
                        type="submit"
                        className="admin-btn primary"
                        disabled={acting === `project-${selectedProject.id}`}
                      >
                        {acting === `project-${selectedProject.id}`
                          ? <FaSpinner className="admin-spin" />
                          : 'Salvar atualização'}
                      </button>
                    </div>

                    <div className="admin-projects-timeline">
                      <strong>Histórico</strong>
                      <ol>
                        {(selectedProject.history || []).slice().reverse().map((event, index) => (
                          <li key={`${event.status}-${event.changedAt}-${index}`}>
                            <span className="admin-projects-timeline-dot" aria-hidden="true" />
                            <div>
                              <div className="admin-projects-timeline-top">
                                <em>{event.label}</em>
                                <small>{formatDate(event.changedAt)}</small>
                              </div>
                              <p>{event.note || 'Atualização de status.'}</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </form>
                )}
              </div>
            )}
          </section>
        )}

        {tab === 'settings' && (
          <section className="admin-card admin-site-settings">
            <div className="admin-card-head">
              <div>
                <span className="admin-card-eyebrow">Operação</span>
                <h2>Configurações do site</h2>
                <p>Controle recursos globais e a disponibilidade pública da Vih&apos;s.</p>
              </div>
              <FaCog className="admin-settings-icon" aria-hidden="true" />
            </div>

            <div className={`admin-setting-row${siteSettings.maintenanceMode ? ' is-enabled' : ''}`}>
              <div className="admin-setting-copy">
                <strong>Modo em breve</strong>
                <p>
                  Exibe a página de manutenção para visitantes e membros. A página de login e
                  contas administrativas autenticadas permanecem liberadas.
                </p>
                <small>
                  Status atual: <b>{siteSettings.maintenanceMode ? 'Ativado' : 'Desativado'}</b>
                </small>
              </div>
              <button
                type="button"
                className={`admin-setting-switch${siteSettings.maintenanceMode ? ' is-on' : ''}`}
                role="switch"
                aria-checked={Boolean(siteSettings.maintenanceMode)}
                aria-label="Alternar modo em breve"
                disabled={settingsLoading}
                onClick={toggleMaintenanceMode}
              >
                <span />
              </button>
            </div>

            <div className={`admin-setting-row${siteSettings.devHubPublicAccess ? ' is-enabled' : ''}`}>
              <div className="admin-setting-copy">
                <strong>Prévia pública do Dev Hub</strong>
                <p>
                  Ativado, libera <b>/dev-hub</b> e os <b>perfis públicos</b> (`/perfil/...`)
                  para todos — logados ou não — e mantém o restante do site restrito.
                  Login e admins continuam com acesso.
                </p>
                <small>
                  Status atual: <b>{siteSettings.devHubPublicAccess ? 'Ativado' : 'Desativado'}</b>
                </small>
              </div>
              <button
                type="button"
                className={`admin-setting-switch${siteSettings.devHubPublicAccess ? ' is-on' : ''}`}
                role="switch"
                aria-checked={Boolean(siteSettings.devHubPublicAccess)}
                aria-label="Alternar prévia pública do Dev Hub"
                disabled={settingsLoading}
                onClick={toggleDevHubPublicAccess}
              >
                <span />
              </button>
            </div>
          </section>
        )}

        {tab === 'badges' && (
          <section className="admin-card">
            <div className="admin-card-head">
              <div>
                <span className="admin-card-eyebrow">Identidade</span>
                <h2>Cargos e badges</h2>
                <p>Crie cargos, defina ícones e cores, ou ajuste os cargos pré-prontos.</p>
              </div>
              <button
                type="button"
                className="admin-btn primary"
                onClick={() => { setBadgeEditor({}); setBadgeForm(EMPTY_BADGE_FORM); }}
              >
                <FaPlus /> Novo cargo
              </button>
            </div>
            {badgeLoading ? <p className="admin-muted">Carregando cargos...</p> : (
              <div className="admin-badge-grid">
                {badges.map((badge) => {
                  const Icon = BADGE_ICONS[badge.icon] || FaAward;
                  return (
                    <button
                      key={badge.id}
                      type="button"
                      className={`admin-badge-card ${badge.active ? '' : 'is-inactive'}`}
                      onClick={() => { setBadgeEditor(badge); setBadgeForm({ ...EMPTY_BADGE_FORM, ...badge }); }}
                    >
                      <span className="admin-role-badge" style={{ color: badge.color, backgroundColor: badge.backgroundColor }}>
                        <Icon /> {badge.name}
                      </span>
                      <small>{badge.description || 'Sem descrição'} {badge.system ? '· Pré-pronto' : ''}</small>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>

      <AnimatePresence>
        {userDrawerOpen && selected && (
          <motion.div
            className="admin-modal-backdrop admin-user-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSelectedId(null)}
          >
            <motion.aside
              className="admin-user-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Ficha do usuário"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              onClick={(event) => event.stopPropagation()}
            >
            <div className="admin-drawer-head">
              <div>
                <p className="admin-eyebrow">Ficha do usuário</p>
                <h2>{selected.profile?.displayName || selected.username}</h2>
                <p>@{selected.username}</p>
              </div>
              <button type="button" className="admin-icon-btn" onClick={() => setSelectedId(null)} aria-label="Fechar">
                <FaTimes />
              </button>
            </div>

            <div className="admin-drawer-profile">
              <div className="admin-avatar lg" aria-hidden="true">
                {selected.avatarUrl
                  ? <img src={selected.avatarUrl} alt="" />
                  : <span>{getInitials(selected.profile?.displayName || selected.username)}</span>}
              </div>
              <dl>
                <div><dt>E-mail</dt><dd>{selected.email}</dd></div>
                <div><dt>Criado em</dt><dd>{formatDate(selected.createdAt)}</dd></div>
                <div><dt>Último acesso</dt><dd>{formatDate(selected.lastLoginAt)}</dd></div>
                <div><dt>Sessões ativas</dt><dd>{selected.activeSessions ?? 0}</dd></div>
                <div><dt>Auth local</dt><dd>{selected.hasLocalAuth ? 'Sim' : 'Não'}</dd></div>
              </dl>
            </div>

            <div className="admin-modal-section">
              <p className="admin-modal-section-title">Ações rápidas</p>
              <div className="admin-action-grid">
              <button
                type="button"
                disabled={acting !== ''}
                onClick={() => runAction(
                  `verified-${selected.id}`,
                  () => userService.updateUserVerification(selected.id, !selected.verified),
                  selected.verified ? 'Selo de verificação removido.' : 'Selo de verificação aplicado.'
                )}
              >
                <FaCheckCircle />
                {selected.verified ? 'Remover selo verificado' : 'Aplicar selo verificado'}
              </button>

              <button
                type="button"
                disabled={acting !== '' || selected.id === me?.id}
                onClick={() => openConfirm({
                  title: selected.role === 'admin' ? 'Remover cargo admin' : 'Promover a admin',
                  description: selected.role === 'admin'
                    ? `Remover o cargo de administrador de @${selected.username}?`
                    : `Promover @${selected.username} a administrador?`,
                  tone: selected.role === 'admin' ? 'warn' : 'ok',
                  key: `role-${selected.id}`,
                  run: () => runAction(
                    `role-${selected.id}`,
                    () => userService.updateUserRole(selected.id, selected.role === 'admin' ? 'user' : 'admin'),
                    'Cargo atualizado.'
                  ),
                })}
              >
                <FaUserShield />
                {selected.role === 'admin' ? 'Tornar membro' : 'Tornar admin'}
              </button>

              <button
                type="button"
                disabled={acting !== '' || selected.id === me?.id}
                onClick={() => openConfirm({
                  title: selected.status === 'active' ? 'Desativar conta' : 'Reativar conta',
                  description: selected.status === 'active'
                    ? `Desativar @${selected.username} e encerrar todas as sessões?`
                    : `Reativar a conta de @${selected.username}?`,
                  tone: selected.status === 'active' ? 'danger' : 'ok',
                  key: `status-${selected.id}`,
                  run: () => runAction(
                    `status-${selected.id}`,
                    () => userService.updateUserStatus(selected.id, selected.status === 'active' ? 'disabled' : 'active'),
                    'Status atualizado.'
                  ),
                })}
              >
                {selected.status === 'active' ? <FaUserSlash /> : <FaUserCheck />}
                {selected.status === 'active' ? 'Desativar' : 'Reativar'}
              </button>

              <button
                type="button"
                className="is-danger"
                disabled={acting !== '' || selected.id === me?.id}
                onClick={() => openConfirm({
                  title: 'Excluir conta permanentemente',
                  description: `Isso remove @${selected.username} do banco de dados, encerra sessões e limpa dados vinculados (posts, follows, etc.). Esta ação não pode ser desfeita.`,
                  tone: 'danger',
                  key: `delete-${selected.id}`,
                  run: async () => {
                    const key = `delete-${selected.id}`;
                    setActing(key);
                    setError('');
                    try {
                      const response = await userService.deleteUser(selected.id);
                      setSelectedId(null);
                      showFlash('success', response?.message || 'Conta excluída.');
                      setConfirm(null);
                      await load(filters);
                    } catch (err) {
                      setError(err.message || 'Falha ao excluir a conta.');
                      showFlash('error', err.message || 'Falha ao excluir a conta.');
                    } finally {
                      setActing('');
                    }
                  },
                })}
              >
                <FaTrashAlt />
                Excluir conta
              </button>

              <button
                type="button"
                disabled={acting !== ''}
                onClick={() => openConfirm({
                  title: 'Forçar nova senha',
                  description: `@${selected.username} verá imediatamente a tela para definir uma nova senha (sem precisar sair da conta). Após salvar, as sessões antigas serão encerradas.`,
                  tone: 'warn',
                  key: `force-${selected.id}`,
                  run: () => runAction(
                    `force-${selected.id}`,
                    () => userService.forcePasswordReset(selected.id),
                    'Reset de senha forçado.'
                  ),
                })}
              >
                <FaKey />
                Forçar reset de senha
              </button>

              {selected.mustChangePassword && (
                <button
                  type="button"
                  disabled={acting !== ''}
                  onClick={() => runAction(
                    `cancel-${selected.id}`,
                    () => userService.cancelPasswordReset(selected.id),
                    'Exigência de senha removida.'
                  )}
                >
                  <FaUserCog />
                  Cancelar reset pendente
                </button>
              )}

              <button
                type="button"
                disabled={acting !== ''}
                onClick={() => openConfirm({
                  title: 'Encerrar sessões',
                  description: `Encerrar todas as sessões ativas de @${selected.username}?`,
                  tone: 'warn',
                  key: `sessions-${selected.id}`,
                  run: () => runAction(
                    `sessions-${selected.id}`,
                    () => userService.revokeUserSessions(selected.id),
                    'Sessões encerradas.'
                  ),
                })}
              >
                <FaDesktop />
                Encerrar sessões
              </button>

              {!selected.emailVerified && (
                <button
                  type="button"
                  disabled={acting !== ''}
                  onClick={() => runAction(
                    `verify-${selected.id}`,
                    () => userService.verifyUserEmail(selected.id),
                    'E-mail verificado.'
                  )}
                >
                  <FaEnvelopeOpenText />
                  Verificar e-mail
                </button>
              )}
              </div>
            </div>

            <div className="admin-modal-section">
            <form
              className="admin-system-form"
              onSubmit={(event) => {
                event.preventDefault();
                runAction(
                  `system-${selected.id}`,
                  () => userService.updateSystemAccount(selected.id, {
                    ...systemForm,
                    operatingSince: brDateToIso(systemForm.operatingSince),
                  }),
                  systemForm.enabled
                    ? 'Conta institucional configurada.'
                    : 'Recursos institucionais removidos.'
                );
              }}
            >
              <div className="admin-system-form-head">
                <div>
                  <p className="admin-card-eyebrow">Conta institucional</p>
                  <h3>Recursos da Vih&apos;s</h3>
                  <p className="admin-system-description">
                    Capa exclusiva, destaque no Explorar e follow automático da comunidade.
                  </p>
                </div>
                <label className={`admin-system-switch${systemForm.enabled ? ' is-on' : ''}`}>
                  <span className="admin-system-switch-copy">
                    <span className="admin-system-switch-label">Conta do sistema</span>
                    <span className="admin-system-switch-state">
                      {systemForm.enabled ? 'Ativa' : 'Inativa'}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={systemForm.enabled}
                    onChange={(event) => setSystemForm((current) => ({ ...current, enabled: event.target.checked }))}
                  />
                  <span className="admin-system-switch-track" aria-hidden="true" />
                </label>
              </div>

              <div className="admin-system-fields">
                <label className="admin-field">
                  <span>Nome de exibição</span>
                  <input
                    value={systemForm.displayName}
                    onChange={(event) => setSystemForm((current) => ({ ...current, displayName: event.target.value }))}
                    maxLength={40}
                  />
                </label>
                <label className="admin-field">
                  <span>Operando desde</span>
                  <input
                    inputMode="numeric"
                    placeholder="DD/MM/AAAA"
                    value={systemForm.operatingSince}
                    onChange={(event) => setSystemForm((current) => ({
                      ...current,
                      operatingSince: maskBrDate(event.target.value),
                    }))}
                  />
                </label>
                <label className="admin-field">
                  <span>Avatar (URL)</span>
                  <input
                    type="url"
                    value={systemForm.avatarUrl}
                    onChange={(event) => setSystemForm((current) => ({ ...current, avatarUrl: event.target.value }))}
                    placeholder="https://..."
                  />
                </label>
                <label className="admin-field">
                  <span>Capa do perfil (URL)</span>
                  <input
                    type="url"
                    value={systemForm.coverUrl}
                    onChange={(event) => setSystemForm((current) => ({ ...current, coverUrl: event.target.value }))}
                    placeholder="https://..."
                  />
                </label>
                <label className="admin-field admin-system-bio">
                  <span>Sobre</span>
                  <textarea
                    value={systemForm.bio}
                    onChange={(event) => setSystemForm((current) => ({ ...current, bio: event.target.value }))}
                    maxLength={280}
                    rows={3}
                  />
                </label>
              </div>

              <div className="admin-system-options">
                <label className="admin-check">
                  <input
                    type="checkbox"
                    checked={systemForm.autoFollow}
                    disabled={!systemForm.enabled}
                    onChange={(event) => setSystemForm((current) => ({ ...current, autoFollow: event.target.checked }))}
                  />
                  <span className="admin-checkbox" aria-hidden="true" />
                  Seguir automaticamente
                </label>
                <label className="admin-check">
                  <input
                    type="checkbox"
                    checked={systemForm.featured}
                    disabled={!systemForm.enabled}
                    onChange={(event) => setSystemForm((current) => ({ ...current, featured: event.target.checked }))}
                  />
                  <span className="admin-checkbox" aria-hidden="true" />
                  Fixar no Explorar
                </label>
                <button type="submit" className="admin-btn primary" disabled={acting !== ''}>
                  Salvar conta institucional
                </button>
              </div>
            </form>
            </div>

            <div className="admin-modal-section admin-user-badges">
              <p className="admin-modal-section-title">Cargos do usuário</p>
              <div>
                {badges.filter((badge) => badge.active).map((badge) => {
                  const assigned = (selected.badges || []).some((item) => item.id === badge.id);
                  const Icon = BADGE_ICONS[badge.icon] || FaAward;
                  return (
                    <button
                      key={badge.id}
                      type="button"
                      className={assigned ? 'is-selected' : ''}
                      disabled={acting !== ''}
                      onClick={() => toggleUserBadge(badge.id)}
                      style={assigned ? { color: badge.color, borderColor: badge.color, backgroundColor: badge.backgroundColor } : {}}
                    >
                      <Icon /> {badge.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {acting && (
              <p className="admin-muted admin-loading-line">
                <FaSpinner className="admin-spin" /> Aplicando ação...
              </p>
            )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {badgeEditor && (
          <motion.aside className="admin-drawer admin-drawer-form" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 28, opacity: 0 }}>
            <div className="admin-drawer-head">
              <div><p className="admin-eyebrow">Cargos e badges</p><h2>{badgeEditor.id ? 'Editar cargo' : 'Novo cargo'}</h2></div>
              <button type="button" className="admin-icon-btn" onClick={() => setBadgeEditor(null)} aria-label="Fechar"><FaTimes /></button>
            </div>
            <form className="admin-form" onSubmit={saveBadge}>
              <label className="admin-field"><span>Nome</span><input required maxLength={32} value={badgeForm.name} onChange={(e) => setBadgeForm((v) => ({ ...v, name: e.target.value }))} /></label>
              <label className="admin-field"><span>Descrição</span><input maxLength={120} value={badgeForm.description} onChange={(e) => setBadgeForm((v) => ({ ...v, description: e.target.value }))} /></label>
              <label className="admin-field"><span>Ícone</span><select value={badgeForm.icon} onChange={(e) => setBadgeForm((v) => ({ ...v, icon: e.target.value }))}>{Object.keys(BADGE_ICONS).map((icon) => <option key={icon} value={icon}>{icon}</option>)}</select></label>
              <div className="admin-form-row">
                <label className="admin-field"><span>Cor do texto</span><input type="color" value={badgeForm.color} onChange={(e) => setBadgeForm((v) => ({ ...v, color: e.target.value }))} /></label>
                <label className="admin-field"><span>Cor de fundo</span><input type="color" value={badgeForm.backgroundColor} onChange={(e) => setBadgeForm((v) => ({ ...v, backgroundColor: e.target.value }))} /></label>
              </div>
              <label className="admin-field"><span>Ordem</span><input type="number" value={badgeForm.order} onChange={(e) => setBadgeForm((v) => ({ ...v, order: e.target.value }))} /></label>
              <label className="admin-check"><input type="checkbox" checked={badgeForm.active} onChange={(e) => setBadgeForm((v) => ({ ...v, active: e.target.checked }))} /><span className="admin-checkbox" aria-hidden="true" /><span>Badge ativo e disponível</span></label>
              <div className="admin-form-actions">
                {badgeEditor.id && !badgeEditor.system && <button type="button" className="admin-btn danger" onClick={async () => { await userService.deleteBadge(badgeEditor.id); setBadgeEditor(null); loadBadges(); }}>Excluir</button>}
                <button type="button" className="admin-btn ghost" onClick={() => setBadgeEditor(null)}>Cancelar</button>
                <button type="submit" className="admin-btn primary" disabled={Boolean(acting)}>Salvar cargo</button>
              </div>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {teamDrawerOpen && (
          <motion.aside
            className="admin-drawer admin-drawer-form"
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 28, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="admin-drawer-head">
              <div>
                <p className="admin-eyebrow">Equipe do Studio</p>
                <h2>{teamEditor.mode === 'create' ? 'Novo membro' : 'Editar membro'}</h2>
                <p>Visível na seção Equipe de /studio</p>
              </div>
              <button type="button" className="admin-icon-btn" onClick={closeTeamEditor} aria-label="Fechar">
                <FaTimes />
              </button>
            </div>

            <form className="admin-form" onSubmit={saveTeamMember}>
              <label className="admin-field">
                <span>Nome</span>
                <input
                  required
                  value={teamForm.name}
                  onChange={(e) => updateTeamField('name', e.target.value)}
                  maxLength={80}
                  placeholder="Nome completo"
                />
              </label>

              <label className="admin-field">
                <span>Cargo</span>
                <input
                  required
                  value={teamForm.role}
                  onChange={(e) => updateTeamField('role', e.target.value)}
                  maxLength={80}
                  placeholder="Ex.: Fundador & Direção"
                />
              </label>

              <label className="admin-field">
                <span>Foco</span>
                <input
                  value={teamForm.focus}
                  onChange={(e) => updateTeamField('focus', e.target.value)}
                  maxLength={120}
                  placeholder="Ex.: Produto · Design · Engenharia"
                />
              </label>

              <label className="admin-field">
                <span>Bio</span>
                <textarea
                  value={teamForm.bio}
                  onChange={(e) => updateTeamField('bio', e.target.value)}
                  maxLength={600}
                  rows={4}
                  placeholder="Breve descrição do membro"
                />
              </label>

              <label className="admin-field">
                <span>Conta vinculada</span>
                <select
                  value={teamForm.linkedUserId}
                  onChange={(e) => handleLinkedUserChange(e.target.value)}
                  disabled={discordUsersLoading}
                >
                  <option value="">Sem vínculo</option>
                  {discordUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.profile?.displayName || user.username}
                      {' '}
                      (@{user.username})
                      {!user.avatarUrl ? ' — sem foto' : ''}
                    </option>
                  ))}
                </select>
                {discordUsersLoading ? (
                  <em className="admin-field-hint">Carregando contas com Discord...</em>
                ) : discordUsers.length === 0 ? (
                  <em className="admin-field-hint">Nenhuma conta ativa com Discord vinculado.</em>
                ) : (
                  <em className="admin-field-hint">Só contas que já têm Discord em /conta.</em>
                )}
              </label>

              <div className={`admin-discord-sync${teamForm.useDiscordAvatar ? ' is-on' : ''}`}>
                <div>
                  <strong>
                    <FaDiscord aria-hidden="true" />
                    Usar foto do Discord
                  </strong>
                  <p>
                    Ao ativar, a foto de perfil vem automaticamente da conta vinculada.
                  </p>
                </div>
                <label className="admin-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(teamForm.useDiscordAvatar)}
                    onChange={(e) => handleDiscordAvatarToggle(e.target.checked)}
                    disabled={!teamForm.linkedUserId}
                  />
                  <span />
                </label>
              </div>

              {teamForm.useDiscordAvatar && linkedDiscordUser ? (
                <div className="admin-discord-sync-preview">
                  {linkedDiscordUser.avatarUrl ? (
                    <img src={normalizeDiscordAvatarUrl(linkedDiscordUser.avatarUrl)} alt="" />
                  ) : (
                    <span>{getInitials(linkedDiscordUser.profile?.displayName || linkedDiscordUser.username)}</span>
                  )}
                  <div>
                    <strong>{linkedDiscordUser.profile?.displayName || linkedDiscordUser.username}</strong>
                    <p>@{linkedDiscordUser.username} · foto sincronizada</p>
                  </div>
                </div>
              ) : (
                <label className="admin-field">
                  <span>Avatar (URL)</span>
                  <input
                    value={teamForm.avatarUrl}
                    onChange={(e) => updateTeamField('avatarUrl', e.target.value)}
                    maxLength={500}
                    placeholder="https://..."
                  />
                </label>
              )}

              <label className="admin-field">
                <span>Discord (URL)</span>
                <input
                  value={teamForm.discordUrl}
                  onChange={(e) => updateTeamField('discordUrl', e.target.value)}
                  maxLength={500}
                  placeholder="Opcional — usa o Discord geral se vazio"
                />
              </label>

              <label className="admin-field">
                <span>Instagram (URL)</span>
                <input
                  value={teamForm.instagramUrl}
                  onChange={(e) => updateTeamField('instagramUrl', e.target.value)}
                  maxLength={500}
                  placeholder="Opcional — usa o Instagram geral se vazio"
                />
              </label>

              <div className="admin-form-row">
                <label className="admin-field">
                  <span>Ordem</span>
                  <input
                    type="number"
                    value={teamForm.order}
                    onChange={(e) => updateTeamField('order', e.target.value)}
                  />
                </label>

                <label className="admin-check">
                  <input
                    type="checkbox"
                    checked={teamForm.active}
                    onChange={(e) => updateTeamField('active', e.target.checked)}
                  />
                  <span className="admin-checkbox" aria-hidden="true" />
                  <span>Visível no Studio</span>
                </label>
              </div>

              {teamForm.avatarUrl && !teamForm.useDiscordAvatar ? (
                <div className="admin-form-preview">
                  <img src={teamForm.avatarUrl} alt="" />
                </div>
              ) : null}

              <div className="admin-form-actions">
                {teamEditor.mode === 'edit' && (
                  <button
                    type="button"
                    className="admin-btn danger"
                    disabled={Boolean(acting)}
                    onClick={() => openConfirm({
                      title: 'Remover membro',
                      description: `Remover ${teamForm.name || 'este membro'} da equipe pública do Studio?`,
                      tone: 'danger',
                      key: `team-delete-${teamEditor.id}`,
                      run: () => deleteTeamMember({ id: teamEditor.id, name: teamForm.name }),
                    })}
                  >
                    <FaTrashAlt aria-hidden="true" />
                    Excluir
                  </button>
                )}
                <button type="button" className="admin-btn ghost" disabled={Boolean(acting)} onClick={closeTeamEditor}>
                  Cancelar
                </button>
                <button type="submit" className="admin-btn primary" disabled={Boolean(acting)}>
                  {isSavingTeam ? <FaSpinner className="admin-spin" /> : null}
                  {teamEditor.mode === 'create' ? 'Adicionar' : 'Salvar'}
                </button>
              </div>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirm && (
          <motion.div
            className="admin-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !acting && setConfirm(null)}
          >
            <motion.div
              className={`admin-modal ${confirm.tone || ''}`}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="admin-confirm-title"
            >
              <h3 id="admin-confirm-title">{confirm.title}</h3>
              <p>{confirm.description}</p>
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn ghost" disabled={Boolean(acting)} onClick={() => setConfirm(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className={`admin-btn ${confirm.tone === 'danger' ? 'danger' : confirm.tone === 'warn' ? 'warn' : 'primary'}`}
                  disabled={Boolean(acting)}
                  onClick={confirm.run}
                >
                  {acting ? <FaSpinner className="admin-spin" /> : null}
                  Confirmar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
